// src/tabs/LancamentosSubTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Plus, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  RefreshCw, 
  AlertCircle, 
  Calendar, 
  Filter, 
  Edit3, 
  CornerUpLeft, 
  HelpCircle,
  TrendingUp,
  TrendingDown,
  Wallet,
  X,
  Check,
  Trash2,
  List,
  LayoutGrid,
  ArrowRightLeft
} from "lucide-react";
import { financeiroService } from "../services/financeiroService";
import { formatCentavos, parseCentavos } from "../utils/moneyUtils";

const centsToDecimalStr = (cents) => {
  if (cents === undefined || cents === null) return "";
  const abs = Math.abs(cents);
  const reais = Math.floor(abs / 100);
  const centavos = abs % 100;
  return `${reais},${String(centavos).padStart(2, "0")}`;
};

export default function LancamentosSubTab({ finance }) {
  const [loading, setLoading] = useState(false);
  const [lancamentos, setLancamentos] = useState([]);
  
  // Pagination & Count
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filters State
  const [period, setPeriod] = useState("este_mes"); // este_mes, mes_anterior, ultimos_30, personalizado
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selCategoria, setSelCategoria] = useState("");
  const [selCentro, setSelCentro] = useState("");
  const [selConta, setSelConta] = useState("");
  const [selStatus, setSelStatus] = useState("");
  const [selTipo, setSelTipo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showEstornos, setShowEstornos] = useState(false);
  const [estornadoIds, setEstornadoIds] = useState(new Set());

  // Modals States
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [editDescricao, setEditDescricao] = useState("");
  const [editValor, setEditValor] = useState("");
  const [editData, setEditData] = useState("");
  const [editCategoria, setEditCategoria] = useState("");
  const [editCentroCusto, setEditCentroCusto] = useState("");
  const [editContaBancaria, setEditContaBancaria] = useState("");
  const [editFormaPagamento, setEditFormaPagamento] = useState("");
  const [editGrupoSemana, setEditGrupoSemana] = useState("");
  const [editDataCompetencia, setEditDataCompetencia] = useState("");
  const [editTipoFluxo, setEditTipoFluxo] = useState("despesa");
  const [editMotivoEstorno, setEditMotivoEstorno] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editTipoCompromisso, setEditTipoCompromisso] = useState("");
  const [isEstornoModalOpen, setIsEstornoModalOpen] = useState(false);
  const [isBaixarModalOpen, setIsBaixarModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Transfer Form States
  const [transfOrigem, setTransfOrigem] = useState("");
  const [transfDestino, setTransfDestino] = useState("");
  const [transfValor, setTransfValor] = useState("");
  const [transfData, setTransfData] = useState(new Date().toISOString().split("T")[0]);
  const [transfDescricao, setTransfDescricao] = useState("Transferência de Saldo");
  
  // Baixar Form States
  const [baixarData, setBaixarData] = useState("");
  const [baixarConta, setBaixarConta] = useState("");
  const [baixarFormaPagamento, setBaixarFormaPagamento] = useState("");

  // View mode states
  const [viewMode, setViewMode] = useState("lista"); // lista, agrupado
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});

  const toggleDay = (day) => {
    setExpandedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const toggleType = (key) => {
    setExpandedTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Função auxiliar para agrupar por Centro de Custo e Categoria
  const groupItemsByCentroAndCategory = useCallback((itemsList) => {
    const groups = {};
    itemsList.forEach((item) => {
      const centroNome = item.expand?.centro_custo?.nome || "Sem Centro";
      const categoriaNome = item.expand?.categoria?.nome || "Sem Categoria";
      const groupKey = `${centroNome} ➔ ${categoriaNome}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          centro: centroNome,
          categoria: categoriaNome,
          subtotal: 0,
          items: [],
        };
      }

      groups[groupKey].items.push(item);
      groups[groupKey].subtotal += Math.abs(item.valor_centavos || 0);
    });
    return Object.values(groups);
  }, []);

  const groupedData = useMemo(() => {
    if (viewMode !== "agrupado") return [];

    const groupsByDate = {};
    const activeLancamentos = showEstornos 
      ? lancamentos 
      : lancamentos.filter(item => item.tipo_lancamento !== "estorno" && !estornadoIds.has(item.id));

    activeLancamentos.forEach((item) => {
      const dateStr = item.data ? item.data.split(" ")[0] : "Sem Data";
      if (!groupsByDate[dateStr]) {
        groupsByDate[dateStr] = {
          date: dateStr,
          receitas: [],
          despesas: [],
          totalReceita: 0,
          totalDespesa: 0,
        };
      }

      const val = item.valor_centavos || 0;
      const isReceita = val > 0;
      const valAbs = Math.abs(val);

      if (isReceita) {
        groupsByDate[dateStr].receitas.push(item);
        groupsByDate[dateStr].totalReceita += valAbs;
      } else {
        groupsByDate[dateStr].despesas.push(item);
        groupsByDate[dateStr].totalDespesa += valAbs;
      }
    });

    const sortedDates = Object.values(groupsByDate).sort((a, b) => b.date.localeCompare(a.date));

    sortedDates.forEach((dayGroup) => {
      dayGroup.receitasGrouped = groupItemsByCentroAndCategory(dayGroup.receitas);
      dayGroup.despesasGrouped = groupItemsByCentroAndCategory(dayGroup.despesas);
    });

    return sortedDates;
  }, [lancamentos, viewMode, groupItemsByCentroAndCategory, showEstornos, estornadoIds]);
  
  // Edit/Estorno Selection
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Manual Form States
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split("T")[0],
    descricao: "",
    valor: "",
    tipo_fluxo: "despesa", // despesa, receita
    categoria: "",
    centro_custo: "",
    conta_bancaria: "",
    forma_pagamento: "",
    status: "pago",
    grupo_semana: "",
    observacoes: ""
  });

  // Notes and Estorno Reason Fields
  const [editNotes, setEditNotes] = useState("");
  const [estornoReason, setEstornoReason] = useState("");

  // Helper para definir datas com base no período selecionado
  const computedDates = useMemo(() => {
    const today = new Date();
    let start = "";
    let end = "";

    if (period === "este_mes") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      start = firstDay.toISOString().split("T")[0];
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
    } else if (period === "mes_anterior") {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      start = firstDay.toISOString().split("T")[0];
      end = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split("T")[0];
    } else if (period === "ultimos_30") {
      const priorDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      start = priorDate.toISOString().split("T")[0];
      end = today.toISOString().split("T")[0];
    } else if (period === "personalizado") {
      start = startDate;
      end = endDate;
    }

    return { start, end };
  }, [period, startDate, endDate]);

  // Carregar lançamentos
  const fetchLancamentos = useCallback(async () => {
    setLoading(true);
    try {
      const activeFilters = {
        startDate: computedDates.start,
        endDate: computedDates.end,
        categoria: selCategoria,
        centro_custo: selCentro,
        conta_bancaria: selConta,
        status: selStatus,
        tipo_lancamento: selTipo,
        search: searchQuery.trim(),
      };

      const res = await financeiroService.getLancamentosPaginated({
        page: viewMode === "agrupado" ? 1 : page,
        perPage: viewMode === "agrupado" ? 300 : 30,
        filters: activeFilters,
      });

      const fetchedItems = res.items || [];
      const itemIds = fetchedItems.map(x => x.id);
      
      let linkedEstornos = [];
      if (itemIds.length > 0) {
        try {
          linkedEstornos = await financeiroService.getEstornosForIds(itemIds);
        } catch (e) {
          console.error("Erro ao buscar estornos vinculados:", e);
        }
      }

      const estornadoSet = new Set();
      fetchedItems.forEach(item => {
        if (item.tipo_lancamento === "estorno" && item.referencia_estorno_de) {
          estornadoSet.add(item.referencia_estorno_de);
        }
      });
      linkedEstornos.forEach(est => {
        if (est.referencia_estorno_de) {
          estornadoSet.add(est.referencia_estorno_de);
        }
      });

      setEstornadoIds(estornadoSet);
      setLancamentos(fetchedItems);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.totalItems || 0);
    } catch (err) {
      console.error("Erro ao buscar lançamentos no ledger:", err);
    } finally {
      setLoading(false);
    }
  }, [page, computedDates, selCategoria, selCentro, selConta, selStatus, selTipo, searchQuery, viewMode]);

  useEffect(() => {
    fetchLancamentos();
  }, [fetchLancamentos]);

  // Resetar página quando alterar filtros
  useEffect(() => {
    setPage(1);
  }, [period, startDate, endDate, selCategoria, selCentro, selConta, selStatus, selTipo, searchQuery, viewMode]);

  // Somatório na tela (indicadores do período exibido/pesquisado)
  const currentPeriodAggregations = useMemo(() => {
    let inflow = 0;
    let outflow = 0;

    // Nota: Como a lista é paginada, estes agregadores na tela representam 
    // os totais dos itens atualmente carregados. É uma estimativa visual.
    lancamentos.forEach((item) => {
      const val = item.valor_centavos || 0;
      if (val > 0) inflow += val;
      else outflow += Math.abs(val);
    });

    return {
      inflow,
      outflow,
      net: inflow - outflow,
    };
  }, [lancamentos]);

  const openCreateModal = () => {
    setFormData({
      data: new Date().toISOString().split("T")[0],
      data_competencia: "",
      descricao: "",
      valor: "",
      tipo_fluxo: "despesa",
      categoria: finance.categorias.filter((c) => c.ativo && c.tipo === "despesa")[0]?.id || "",
      centro_custo: finance.centrosCusto.filter(c => c.ativo)[0]?.id || "",
      conta_bancaria: finance.contasBancarias.filter(c => c.ativo)[0]?.id || "",
      forma_pagamento: "",
      status: "pago",
      grupo_semana: "",
      observacoes: ""
    });
    setIsManualModalOpen(true);
  };

  // Gravar Lançamento Manual
  const handleCreateManual = async (e) => {
    e.preventDefault();
    if (!formData.descricao.trim() || !formData.valor || !formData.categoria || !formData.centro_custo) {
      alert("Preencha todos os campos obrigatórios (*).");
      return;
    }

    const valueMultiplier = formData.tipo_fluxo === "despesa" ? -1 : 1;
    const centsValue = parseCentavos(formData.valor) * valueMultiplier;

    const payload = {
      data: formData.data,
      data_competencia: formData.data_competencia || null,
      descricao: formData.descricao.trim(),
      valor_centavos: centsValue,
      categoria: formData.categoria,
      centro_custo: formData.centro_custo,
      conta_bancaria: formData.conta_bancaria || null,
      forma_pagamento: formData.forma_pagamento.trim() || null,
      status: formData.status,
      grupo_semana: formData.grupo_semana.trim() || null,
      observacoes: formData.observacoes.trim() || null,
    };

    try {
      finance.setLoading(true);
      await financeiroService.createLancamentoManual(payload, finance.user);
      setIsManualModalOpen(false);
      await fetchLancamentos();
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
    } catch (err) {
      let msg = err.message;
      if (err.response?.data) {
        const details = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${v.message || JSON.stringify(v)}`)
          .join("\n");
        msg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao criar lançamento: " + msg);
    } finally {
      finance.setLoading(false);
    }
  };

  // Gravar edição de lançamentos (Respeitando I1, R1, R10)
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;

    const centsValue = parseCentavos(editValor) * (editTipoFluxo === "despesa" ? -1 : 1);
    const valueChanged = centsValue !== selectedItem.valor_centavos;
    const descChanged = editDescricao.trim() !== (selectedItem.descricao || "").trim();
    const dateChanged = editData !== selectedItem.data;
    
    // É histórico se tiver transação vinculada ou o mês estiver fechado
    const isHistory = !!(selectedItem.transacao_bancaria || finance.isPeriodoFechado(selectedItem.data));

    try {
      finance.setLoading(true);

      if (isHistory && (valueChanged || descChanged || dateChanged)) {
        if (!editMotivoEstorno.trim()) {
          alert("Por favor, informe o motivo da correção (estorno).");
          finance.setLoading(false);
          return;
        }

        // 1. Estorna o atual
        await financeiroService.estornarLancamento(selectedItem.id, editMotivoEstorno.trim(), finance.user);

        // 2. Cria o novo lançamento correto
        const payload = {
          data: editData,
          data_competencia: editDataCompetencia || null,
          descricao: editDescricao.trim(),
          valor_centavos: centsValue,
          categoria: editCategoria,
          centro_custo: editCentroCusto,
          status: editStatus || selectedItem.status,
          tipo_compromisso: editTipoCompromisso || null,
          conta_bancaria: editContaBancaria || null,
          forma_pagamento: editFormaPagamento.trim() || null,
          grupo_semana: editGrupoSemana.trim() || null,
          observacoes: editNotes.trim() || null
        };
        await financeiroService.createLancamentoManual(payload, finance.user, finance.bloqueioRigido);
        alert("Lançamento histórico estornado e corrigido com sucesso!");
      } else {
        // Edição direta permitida (seja de metadados contábeis ou de lançamentos normais em aberto)
        const payload = {
          categoria: editCategoria,
          centro_custo: editCentroCusto,
          status: editStatus,
          tipo_compromisso: editTipoCompromisso || null,
          conta_bancaria: editContaBancaria || null,
          forma_pagamento: editFormaPagamento.trim() || null,
          grupo_semana: editGrupoSemana.trim() || null,
          data_competencia: editDataCompetencia || null,
          observacoes: editNotes.trim() || null
        };

        // Se não for histórico, pode editar valor, descrição e data diretamente
        if (!isHistory) {
          payload.descricao = editDescricao.trim();
          payload.valor_centavos = centsValue;
          payload.data = editData;
        }

        await financeiroService.updateLancamento(selectedItem.id, payload, finance.user, finance.bloqueioRigido);
        alert("Lançamento atualizado com sucesso!");
      }

      setIsNotesModalOpen(false);
      await fetchLancamentos();
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
    } catch (err) {
      let msg = err.message;
      if (err.response?.data) {
        const details = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${v.message || JSON.stringify(v)}`)
          .join("\n");
        msg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao atualizar lançamento: " + msg);
    } finally {
      finance.setLoading(false);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setEditDescricao(item.descricao || "");
    setEditValor(centsToDecimalStr(item.valor_centavos));
    setEditData(item.data ? item.data.substring(0, 10) : "");
    setEditCategoria(item.categoria || "");
    setEditCentroCusto(item.centro_custo || "");
    setEditContaBancaria(item.conta_bancaria || "");
    setEditFormaPagamento(item.forma_pagamento || "");
    setEditGrupoSemana(item.grupo_semana || "");
    setEditDataCompetencia(item.data_competencia ? item.data_competencia.substring(0, 10) : "");
    setEditNotes(item.observacoes || "");
    setEditTipoFluxo(item.valor_centavos < 0 ? "despesa" : "receita");
    setEditStatus(item.status || "");
    setEditTipoCompromisso(item.tipo_compromisso || "");
    setEditMotivoEstorno("");
    setIsNotesModalOpen(true);
  };

  // Gravar estorno cronológico (Respeitando I2)
  const handleTriggerEstorno = async (e) => {
    e.preventDefault();
    if (!selectedItem || !estornoReason.trim()) return;

    try {
      finance.setLoading(true);
      await financeiroService.estornarLancamento(selectedItem.id, estornoReason.trim(), finance.user);
      setIsEstornoModalOpen(false);
      await fetchLancamentos();
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
    } catch (err) {
      let msg = err.message;
      if (err.response?.data) {
        const details = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${v.message || JSON.stringify(v)}`)
          .join("\n");
        msg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao realizar estorno: " + msg);
    } finally {
      finance.setLoading(false);
    }
  };

  // Baixar / Confirmar Pagamento (Liquidação)
  const openBaixarModal = (item) => {
    setSelectedItem(item);
    setBaixarData(new Date().toISOString().split("T")[0]);
    setBaixarConta(item.conta_bancaria || finance.contasBancarias.filter(c => c.ativo)[0]?.id || "");
    setBaixarFormaPagamento(item.forma_pagamento || "");
    setIsBaixarModalOpen(true);
  };

  const handleBaixarSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;

    try {
      finance.setLoading(true);
      await financeiroService.updateLancamento(
        selectedItem.id,
        {
          status: "pago",
          conta_bancaria: baixarConta || null,
          data: baixarData,
          forma_pagamento: baixarFormaPagamento.trim() || null,
        },
        finance.user
      );
      setIsBaixarModalOpen(false);
      await fetchLancamentos();
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
    } catch (err) {
      let msg = err.message;
      if (err.response?.data) {
        const details = Object.entries(err.response.data)
          .map(([k, v]) => `${k}: ${v.message || JSON.stringify(v)}`)
          .join("\n");
        msg += "\n\nDetalhes:\n" + details;
      }
      alert("Erro ao liquidar lançamento: " + msg);
    } finally {
      finance.setLoading(false);
    }
  };

  const handleDeleteLancamento = async (item) => {
    if (!window.confirm(`Tem certeza que deseja EXCLUIR permanentemente o lançamento "${item.descricao}"? Esta ação não pode ser desfeita e removerá o registro do Ledger.`)) return;

    try {
      finance.setLoading(true);
      await financeiroService.deleteLancamento(item.id, finance.user);
      await fetchLancamentos();
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
    } catch (err) {
      alert("Erro ao excluir lançamento: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!transfOrigem || !transfDestino) {
      alert("Por favor, selecione as contas de origem e destino.");
      return;
    }
    if (transfOrigem === transfDestino) {
      alert("A conta de destino não pode ser igual à conta de origem.");
      return;
    }
    const cents = parseCentavos(transfValor);
    if (!cents || cents <= 0) {
      alert("Por favor, informe um valor de transferência válido.");
      return;
    }

    try {
      finance.setLoading(true);
      await finance.createTransferencia({
        contaOrigemId: transfOrigem,
        contaDestinoId: transfDestino,
        valorCentavos: cents,
        data: transfData,
        descricao: transfDescricao,
      });

      setIsTransferModalOpen(false);
      alert("Transferência realizada com sucesso!");
      await fetchLancamentos();
    } catch (err) {
      alert("Erro ao realizar transferência: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">

      {/* ─── SALDOS DAS CONTAS BANCÁRIAS (REAL E TRANSITÓRIO) ────────────────── */}
      <div className="flex gap-4 p-4 lg:p-6 pb-0 overflow-x-auto shrink-0 [&::-webkit-scrollbar]:hidden bg-slate-50">
        {finance.contasBancarias.map((c) => {
          const saldoCents = (c.saldo_inicial_centavos || 0) + (finance.saldosContas[c.id] || 0);
          const isVirtual = c.nome.toLowerCase().includes("adquirente") || c.nome.toLowerCase().includes("mercado") || c.nome.toLowerCase().includes("stone") || c.nome.toLowerCase().includes("transito");
          
          return (
            <div 
              key={c.id} 
              className={`bg-white border rounded-xl p-3.5 shadow-xs min-w-[200px] flex-1 flex flex-col justify-between transition-all cursor-pointer select-none ${
                selConta === c.id ? "border-blue-500 bg-blue-50/5 ring-1 ring-blue-500" : "border-slate-200 hover:border-slate-350"
              }`}
              onClick={() => setSelConta(selConta === c.id ? "" : c.id)}
              role="button"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] font-black text-slate-550 uppercase tracking-wider truncate">
                  {c.nome}
                </span>
                {isVirtual ? (
                  <span className="text-[8px] font-black uppercase bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200 shrink-0">
                    Virtual
                  </span>
                ) : (
                  <span className="text-[8px] font-black uppercase bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded border border-blue-200 shrink-0">
                    Real
                  </span>
                )}
              </div>
              <div className="mt-2.5">
                <span className={`text-sm font-black font-mono ${saldoCents >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                  {formatCentavos(saldoCents)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* ─── CARDS DE RESUMO (DO PERÍODO DA TELA) ────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 lg:p-6 pb-0 shrink-0">
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Receitas Carregadas</span>
              <h4 className="text-lg font-black text-slate-800 font-mono">{formatCentavos(currentPeriodAggregations.inflow)}</h4>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-rose-50 text-rose-600 rounded-lg flex items-center justify-center">
              <TrendingDown size={20} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Despesas Carregadas</span>
              <h4 className="text-lg font-black text-slate-800 font-mono text-rose-600">{formatCentavos(currentPeriodAggregations.outflow)}</h4>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
              <Wallet size={20} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Resultado na Página</span>
              <h4 className={`text-lg font-black font-mono ${currentPeriodAggregations.net >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                {formatCentavos(currentPeriodAggregations.net)}
              </h4>
            </div>
          </div>
        </div>
      </div>

      {/* ─── FILTROS DE LEDGER ──────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          
          {/* Período */}
          <div className="flex items-center gap-1.5 min-w-[200px]">
            <Calendar size={16} className="text-slate-400 shrink-0" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full text-xs font-bold text-slate-700 uppercase bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="este_mes">Este Mês</option>
              <option value="mes_anterior">Mês Anterior</option>
              <option value="ultimos_30">Últimos 30 Dias</option>
              <option value="personalizado">Período Customizado</option>
            </select>
          </div>

          {/* Filtros Customizados Datas */}
          {period === "personalizado" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg p-2 outline-none"
              />
              <span className="text-xs font-bold text-slate-400">até</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg p-2 outline-none"
              />
            </div>
          )}

          {/* Categoria */}
          <select
            value={selCategoria}
            onChange={(e) => setSelCategoria(e.target.value)}
            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
          >
            <option value="">Todas Categorias</option>
            {finance.categorias.map((c) => (
              <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
            ))}
          </select>

          {/* Centro Custo */}
          <select
            value={selCentro}
            onChange={(e) => setSelCentro(e.target.value)}
            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
          >
            <option value="">Todos Centros</option>
            {finance.centrosCusto.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>

          {/* Conta Bancária */}
          <select
            value={selConta}
            onChange={(e) => setSelConta(e.target.value)}
            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
          >
            <option value="">Todas Contas</option>
            {finance.contasBancarias.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>

          {/* Status */}
          <select
            value={selStatus}
            onChange={(e) => setSelStatus(e.target.value)}
            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
          >
            <option value="">Todos Status</option>
            <option value="pago">Pago / Confirmado</option>
            <option value="a_vencer">A Vencer</option>
            <option value="provisao">Provisão</option>
          </select>

          {/* Tipo */}
          <select
            value={selTipo}
            onChange={(e) => setSelTipo(e.target.value)}
            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
          >
            <option value="">Todos Tipos</option>
            <option value="normal">Normal</option>
            <option value="estorno">Estornos</option>
          </select>
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          {/* Busca Textual */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Pesquisar por descrição..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg outline-none text-xs"
            />
          </div>

          <label className="flex items-center gap-1.5 cursor-pointer bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-600 select-none">
            <input
              type="checkbox"
              checked={showEstornos}
              onChange={(e) => setShowEstornos(e.target.checked)}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Mostrar Estornos
          </label>
          
          <div className="flex bg-slate-100 border border-slate-200 p-0.5 rounded-lg shrink-0">
            <button
              type="button"
              onClick={() => setViewMode("lista")}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
                viewMode === "lista"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              title="Exibição em Lista Tradicional"
            >
              <List size={14} /> Lista
            </button>
            <button
              type="button"
              onClick={() => setViewMode("agrupado")}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
                viewMode === "agrupado"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              title="Exibição Agrupada por Dia"
            >
              <LayoutGrid size={14} /> Agrupado
            </button>
          </div>

          <button
            onClick={() => {
              setTransfOrigem("");
              setTransfDestino("");
              setTransfValor("");
              setTransfData(new Date().toISOString().split("T")[0]);
              setTransfDescricao("Transferência de Saldo");
              setIsTransferModalOpen(true);
            }}
            className="flex items-center gap-1.5 bg-slate-150 hover:bg-slate-200 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-xs font-bold shadow-sm transition-colors shrink-0"
          >
            <ArrowRightLeft size={14} /> Transferir
          </button>

          <button
            onClick={openCreateModal}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors shrink-0"
          >
            <Plus size={16} /> Lançar Manual
          </button>
        </div>
      </div>

      {/* ─── TABELA DE LEDGER ───────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-white p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
            <span className="text-xs font-semibold text-slate-400">Carregando Ledger...</span>
          </div>
        ) : viewMode === "agrupado" ? (
          /* Variação agrupada sanfona */
          <div className="space-y-4 max-w-5xl mx-auto pb-8">
            {groupedData.map((dayGroup, idx) => {
              const dateStr = dayGroup.date;
              const isDayExpanded = expandedDays[dateStr] !== undefined ? expandedDays[dateStr] : idx === 0;

              return (
                <div key={dateStr} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm transition-all hover:border-slate-300">
                  {/* DIA HEADER */}
                  <div
                    onClick={() => toggleDay(dateStr)}
                    className="flex justify-between items-center px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100/70 transition-colors select-none"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-black text-slate-700 bg-slate-200 px-3 py-1 rounded-full font-mono">
                        {dateStr && dateStr !== "Sem Data" ? dateStr.substring(0, 10).split("-").reverse().join("/") : dateStr}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      {dayGroup.totalReceita > 0 && (
                        <span className="text-xs font-black text-emerald-700 font-mono">
                          + {formatCentavos(dayGroup.totalReceita)}
                        </span>
                      )}
                      {dayGroup.totalDespesa > 0 && (
                        <span className="text-xs font-black text-rose-600 font-mono">
                          - {formatCentavos(dayGroup.totalDespesa)}
                        </span>
                      )}
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wide bg-white px-2 py-0.5 border border-slate-200 rounded">
                        {isDayExpanded ? "Recolher" : "Expandir"}
                      </span>
                    </div>
                  </div>

                  {/* DIA BODY */}
                  {isDayExpanded && (
                    <div className="p-4 space-y-4 bg-white">
                      
                      {/* SEÇÃO RECEITAS */}
                      {dayGroup.receitas.length > 0 && (
                        <div className="pt-1">
                          <div
                            onClick={() => toggleType(`${dateStr}-receitas`)}
                            className="flex justify-between items-center py-2 px-3 bg-emerald-50/50 border border-emerald-100/70 hover:bg-emerald-50 rounded-lg cursor-pointer transition-colors select-none mb-3"
                          >
                            <span className="text-xs font-black text-emerald-800 uppercase tracking-wide">
                              Receitas (+ {formatCentavos(dayGroup.totalReceita)})
                            </span>
                            <span className="text-[10px] font-bold text-emerald-600">
                              {expandedTypes[`${dateStr}-receitas`] !== false ? "Recolher" : "Expandir"}
                            </span>
                          </div>

                          {expandedTypes[`${dateStr}-receitas`] !== false && (
                            <div className="space-y-3 pl-1 sm:pl-3">
                              {dayGroup.receitasGrouped.map((grp) => (
                                <div key={grp.key} className="border border-slate-150 rounded-xl p-3 bg-slate-50/30">
                                  <div className="flex justify-between items-center border-b border-slate-200/50 pb-2 mb-2">
                                    <span className="text-xs font-black text-slate-700 uppercase tracking-wide">
                                      {grp.centro} ➔ <span className="text-slate-500">{grp.categoria}</span>
                                    </span>
                                    <span className="text-xs font-black text-emerald-700 font-mono">
                                      + {formatCentavos(grp.subtotal)}
                                    </span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs text-slate-650 font-semibold">
                                      <tbody>
                                        {grp.items.map((item) => (
                                          <tr key={item.id} className="hover:bg-slate-100/50 border-b border-slate-100/30">
                                            <td className="py-2.5 pr-4 font-semibold text-slate-800 max-w-xs sm:max-w-none truncate">{item.descricao}</td>
                                            <td className="py-2.5 pr-4 text-[10px] font-bold text-slate-400 font-mono">{item.expand?.conta_bancaria?.nome || "-"}</td>
                                            <td className="py-2.5 pr-4 text-emerald-700 font-mono font-bold text-right whitespace-nowrap">+ {formatCentavos(Math.abs(item.valor_centavos))}</td>
                                            <td className="py-2.5 pl-4 text-right">
                                              <div className="flex items-center justify-end gap-1.5">
                                                <button
                                                  onClick={() => openEditModal(item)}
                                                  className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                                                  title="Editar Lançamento"
                                                >
                                                  <Edit3 size={13} />
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteLancamento(item)}
                                                  className="text-slate-400 hover:text-rose-600 transition-colors p-1"
                                                  title="Excluir"
                                                >
                                                  <Trash2 size={13} />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* SEÇÃO DESPESAS */}
                      {dayGroup.despesas.length > 0 && (
                        <div className="pt-2">
                          <div
                            onClick={() => toggleType(`${dateStr}-despesas`)}
                            className="flex justify-between items-center py-2 px-3 bg-rose-50/50 border border-rose-100/70 hover:bg-rose-50 rounded-lg cursor-pointer transition-colors select-none mb-3"
                          >
                            <span className="text-xs font-black text-rose-800 uppercase tracking-wide">
                              Despesas (- {formatCentavos(dayGroup.totalDespesa)})
                            </span>
                            <span className="text-[10px] font-bold text-rose-600">
                              {expandedTypes[`${dateStr}-despesas`] !== false ? "Recolher" : "Expandir"}
                            </span>
                          </div>

                          {expandedTypes[`${dateStr}-despesas`] !== false && (
                            <div className="space-y-3 pl-1 sm:pl-3">
                              {dayGroup.despesasGrouped.map((grp) => (
                                <div key={grp.key} className="border border-slate-150 rounded-xl p-3 bg-slate-50/30">
                                  <div className="flex justify-between items-center border-b border-slate-200/50 pb-2 mb-2">
                                    <span className="text-xs font-black text-slate-700 uppercase tracking-wide">
                                      {grp.centro} ➔ <span className="text-slate-500">{grp.categoria}</span>
                                    </span>
                                    <span className="text-xs font-black text-rose-600 font-mono">
                                      - {formatCentavos(grp.subtotal)}
                                    </span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs text-slate-650 font-semibold">
                                      <tbody>
                                        {grp.items.map((item) => (
                                          <tr key={item.id} className="hover:bg-slate-100/50 border-b border-slate-100/30">
                                            <td className="py-2.5 pr-4 font-semibold text-slate-800 max-w-xs sm:max-w-none truncate">{item.descricao}</td>
                                            <td className="py-2.5 pr-4 text-[10px] font-bold text-slate-400 font-mono">{item.expand?.conta_bancaria?.nome || "-"}</td>
                                            <td className="py-2.5 pr-4 text-rose-600 font-mono font-bold text-right whitespace-nowrap">- {formatCentavos(Math.abs(item.valor_centavos))}</td>
                                            <td className="py-2.5 pl-4 text-right">
                                              <div className="flex items-center justify-end gap-1.5">
                                                <button
                                                  onClick={() => openEditModal(item)}
                                                  className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                                                  title="Editar Lançamento"
                                                >
                                                  <Edit3 size={13} />
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteLancamento(item)}
                                                  className="text-slate-400 hover:text-rose-600 transition-colors p-1"
                                                  title="Excluir"
                                                >
                                                  <Trash2 size={13} />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 w-28">Data</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Descrição</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Categoria</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Centro</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Conta</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Valor</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-24">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lancamentos
                  .filter(item => showEstornos || (item.tipo_lancamento !== "estorno" && !estornadoIds.has(item.id)))
                  .map((item) => {
                  const isEstorno = item.tipo_lancamento === "estorno";
                  const isReceita = item.valor_centavos > 0;
                  return (
                    <tr key={item.id} className={`hover:bg-slate-50/50 ${isEstorno ? "bg-amber-50/10" : ""}`}>
                      <td className="px-4 py-3 text-xs font-semibold text-slate-500 font-mono">
                        {item.data ? item.data.substring(0, 10).split("-").reverse().join("/") : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className={`text-sm font-bold text-slate-800 ${(isEstorno || estornadoIds.has(item.id)) ? "line-through text-slate-400" : ""}`}>
                            {item.descricao}
                            {estornadoIds.has(item.id) && (
                              <span className="ml-2 bg-rose-100 text-rose-800 text-[9px] font-black uppercase px-1.5 py-0.5 rounded tracking-wide border border-rose-200">
                                ESTORNADO
                              </span>
                            )}
                          </span>
                          {item.observacoes && (
                            <span className="text-[10px] text-slate-450 italic mt-0.5 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5 self-start">
                              Obs: {item.observacoes}
                            </span>
                          )}
                          {isEstorno && (
                            <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200/50 rounded px-1.5 py-0.2 self-start mt-1">
                              ESTORNO DA TRANSACAO
                            </span>
                          )}
                          {item.referencia_estorno_de && (
                            <span className="text-[9px] text-slate-400 font-medium mt-0.5">
                              ID Ref: {item.referencia_estorno_de}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold text-slate-600">
                          {item.expand?.categoria?.nome || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold text-slate-550">
                          {item.expand?.centro_custo?.nome || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold text-slate-500">
                          {item.expand?.conta_bancaria?.nome || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm font-bold">
                        <span className={isReceita ? "text-emerald-700" : "text-rose-600"}>
                          {isReceita ? "+" : "-"} {formatCentavos(Math.abs(item.valor_centavos))}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.status === "pago" ? (
                          <span className="inline-block px-2.5 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-black uppercase tracking-wider">
                            Pago
                          </span>
                        ) : (
                          <button
                            onClick={() => openBaixarModal(item)}
                            title="Clique para Baixar / Confirmar Pagamento"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 text-[10px] font-black uppercase tracking-wider transition-all border border-amber-300 animate-pulse"
                          >
                            PAGAR
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openEditModal(item)}
                            title="Editar Lançamento"
                            className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                          >
                            <Edit3 size={15} />
                          </button>

                          {item.status !== "pago" && !isEstorno && (
                            <button
                              onClick={() => openBaixarModal(item)}
                              title="Baixar / Confirmar Pagamento"
                              className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                            >
                              <Check size={15} />
                            </button>
                          )}
                          
                          {!isEstorno && (
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setEstornoReason("");
                                setIsEstornoModalOpen(true);
                              }}
                              title="Estornar Transação"
                              className="text-slate-400 hover:text-amber-600 transition-colors p-1"
                            >
                              <CornerUpLeft size={15} />
                            </button>
                          )}

                          <button
                            onClick={() => handleDeleteLancamento(item)}
                            title="Excluir Lançamento"
                            className="text-slate-400 hover:text-rose-600 transition-colors p-1"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {lancamentos.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-xs font-medium text-slate-400">
                      Nenhum lançamento encontrado no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── BARRA DE PAGINAÇÃO ────────────────────────────────────────── */}
      <div className="bg-slate-50 border-t border-slate-200 p-3 shrink-0 flex items-center justify-between">
        <span className="text-xs text-slate-500 font-bold">
          Total de lançamentos: {totalItems}
        </span>

        {viewMode !== "agrupado" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 border border-slate-200 rounded bg-white hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>
            
            <span className="text-xs font-bold text-slate-700 font-mono">
              {page} / {totalPages}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1 border border-slate-200 rounded bg-white hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ─── MODAL: LANÇAMENTO MANUAL ───────────────────────────────────── */}
      {isManualModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                <Plus size={18} /> Novo Lançamento Manual
              </h4>
              <button onClick={() => setIsManualModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateManual} className="p-5 flex-1 space-y-4 max-h-[80vh] overflow-y-auto">
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data (Caixa) *</label>
                  <input
                    type="date"
                    required
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Competência</label>
                  <input
                    type="date"
                    value={formData.data_competencia}
                    onChange={(e) => setFormData({ ...formData, data_competencia: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo de Fluxo *</label>
                  <select
                    value={formData.tipo_fluxo}
                    onChange={(e) => setFormData({ ...formData, tipo_fluxo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="despesa">Despesa (Saída)</option>
                    <option value="receita">Receita (Entrada)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descrição / Histórico *</label>
                <input
                  type="text"
                  required
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="Ex: Compra de suprimentos escritório, Venda PDV"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Valor (R$) *</label>
                  <input
                    type="text"
                    required
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold font-mono text-right"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Status Lançamento *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="pago">Pago / Recebido</option>
                    <option value="a_vencer">A Vencer</option>
                    <option value="provisao">Provisão</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                  <select
                    required
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="" disabled>Selecione...</option>
                    {finance.categorias
                      .filter((c) => c.ativo && c.tipo === formData.tipo_fluxo)
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Centro de Custo *</label>
                  <select
                    required
                    value={formData.centro_custo}
                    onChange={(e) => setFormData({ ...formData, centro_custo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="" disabled>Selecione...</option>
                    {finance.centrosCusto.filter(c => c.ativo).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Conta Bancária</label>
                  <select
                    value={formData.conta_bancaria}
                    onChange={(e) => setFormData({ ...formData, conta_bancaria: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="">Nenhuma...</option>
                    {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                  <input
                    type="text"
                    value={formData.forma_pagamento}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Pix, Cartão, Dinheiro..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Grupo Semana (Caixinha)</label>
                  <input
                    type="text"
                    value={formData.grupo_semana}
                    onChange={(e) => setFormData({ ...formData, grupo_semana: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Semana 1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Observações Internas</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Informações adicionais para auditoria..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsManualModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Salvar Lançamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: EDITAR LANÇAMENTO ──────────────────────────────────── */}
      {isNotesModalOpen && selectedItem && (() => {
        const centsValue = parseCentavos(editValor) * (editTipoFluxo === "despesa" ? -1 : 1);
        const valueChanged = centsValue !== selectedItem.valor_centavos;
        const descChanged = editDescricao.trim() !== (selectedItem.descricao || "").trim();
        const dateChanged = editData !== selectedItem.data;
        const isHistory = !!(selectedItem.transacao_bancaria || finance.isPeriodoFechado(selectedItem.data));
        const requiresEstorno = isHistory && (valueChanged || descChanged || dateChanged);

        return (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden flex flex-col relative">
              
              <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
                <h4 className="font-bold text-sm uppercase tracking-wide">Editar Lançamento</h4>
                <button onClick={() => setIsNotesModalOpen(false)} className="text-slate-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSaveEdit} className="p-5 space-y-4 overflow-y-auto max-h-[75vh]">
                
                {requiresEstorno && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1 animate-fade-in">
                    <strong className="block uppercase tracking-wider text-[10px]">Aviso de Estorno Automático</strong>
                    <p className="leading-relaxed">
                      Este lançamento já está conciliado com o banco ou pertence a um mês fechado. Alterações de valor, descrição ou data caixa irão gerar um <strong>estorno de ajuste negativo</strong> no ledger e um novo lançamento corrigido.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Descrição / Credor *</label>
                  <input
                    type="text"
                    required
                    value={editDescricao}
                    onChange={(e) => setEditDescricao(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Valor (R$) *</label>
                    <input
                      type="text"
                      required
                      value={editValor}
                      onChange={(e) => setEditValor(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold text-right"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Competência</label>
                    <input
                      type="date"
                      value={editDataCompetencia}
                      onChange={(e) => setEditDataCompetencia(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Fluxo *</label>
                    <select
                      value={editTipoFluxo}
                      onChange={(e) => setEditTipoFluxo(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      <option value="despesa">Despesa (Saída)</option>
                      <option value="receita">Receita (Entrada)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data (Caixa) *</label>
                    <input
                      type="date"
                      required
                      value={editData}
                      onChange={(e) => setEditData(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                    <select
                      value={editCategoria}
                      onChange={(e) => setEditCategoria(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      {finance.categorias.filter((c) => c.ativo).map((c) => (
                        <option key={c.id} value={c.id}>
                          [{c.tipo === "receita" ? "Receita" : "Despesa"}] {c.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Centro de Custo *</label>
                    <select
                      value={editCentroCusto}
                      onChange={(e) => setEditCentroCusto(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      {finance.centrosCusto.filter((cc) => cc.ativo).map((cc) => (
                        <option key={cc.id} value={cc.id}>{cc.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Conta Bancária</label>
                    <select
                      value={editContaBancaria}
                      onChange={(e) => setEditContaBancaria(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      <option value="">Sem Conta / Dinheiro</option>
                      {finance.contasBancarias.filter((cb) => cb.ativo).map((cb) => (
                        <option key={cb.id} value={cb.id}>{cb.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                    <input
                      type="text"
                      value={editFormaPagamento}
                      onChange={(e) => setEditFormaPagamento(e.target.value)}
                      placeholder="Pix, Boleto, etc."
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Grupo Semana</label>
                    <input
                      type="text"
                      value={editGrupoSemana}
                      onChange={(e) => setEditGrupoSemana(e.target.value)}
                      placeholder="Ex: Semana 1"
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Status / Situação *</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      <option value="pago">Pago / Confirmado</option>
                      <option value="a_vencer">A Vencer</option>
                      <option value="provisao">Provisão</option>
                      <option value="pendente_conciliacao">Pendente Conciliação</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Tipo de Compromisso</label>
                    <select
                      value={editTipoCompromisso}
                      onChange={(e) => setEditTipoCompromisso(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      <option value="">Nenhum...</option>
                      {finance.tiposCompromisso && finance.tiposCompromisso.filter(t => t.ativo !== false).map((t) => (
                        <option key={t.id} value={t.id}>{t.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Observações / Notas</label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 h-16 resize-none"
                    placeholder="Escreva anotações internas..."
                  />
                </div>

                {requiresEstorno && (
                  <div>
                    <label className="block text-[10px] font-bold text-rose-600 uppercase mb-1">Motivo da Correção (Estorno) *</label>
                    <input
                      type="text"
                      required
                      value={editMotivoEstorno}
                      onChange={(e) => setEditMotivoEstorno(e.target.value)}
                      placeholder="Descreva o motivo desta correção contábil"
                      className="w-full px-3 py-2 border border-rose-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-rose-500 font-semibold text-rose-800"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsNotesModalOpen(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-1 bg-blue-650 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                  >
                    <Check size={16} /> {requiresEstorno ? "Estornar & Corrigir" : "Salvar Alterações"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* ─── MODAL: CONFIRMAÇÃO DE ESTORNO ─────────────────────────────── */}
      {isEstornoModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Solicitar Estorno</h4>
              <button onClick={() => setIsEstornoModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleTriggerEstorno} className="p-5 space-y-4">
              <div className="flex gap-2.5 bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg text-xs font-medium">
                <AlertCircle size={20} className="shrink-0 text-amber-600 mt-0.5" />
                <div>
                  <strong>Atenção:</strong> Lançamentos consolidados são imutáveis. Esta ação criará um <strong>novo lançamento de estorno</strong> com valor invertido referenciando esta transação.
                </div>
              </div>

              <div>
                <span className="text-xs font-bold text-slate-400 block mb-1">Transação a Estornar:</span>
                <span className="text-xs font-black text-slate-700 block">{selectedItem?.descricao}</span>
                <span className="text-xs font-black text-rose-600 block mb-4 font-mono">
                  {formatCentavos(selectedItem?.valor_centavos)}
                </span>

                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Motivo do Estorno *</label>
                <input
                  type="text"
                  required
                  value={estornoReason}
                  onChange={(e) => setEstornoReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="EX: Cancelamento de pedido, erro de digitação"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEstornoModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <CornerUpLeft size={16} /> Confirmar Estorno
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: BAIXAR LANÇAMENTO (CONFIRMAR PAGAMENTO) ─────────────── */}
      {isBaixarModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Liquidar Lançamento (Marcar Pago)</h4>
              <button onClick={() => setIsBaixarModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleBaixarSubmit} className="p-5 space-y-4">
              <div>
                <span className="text-xs font-bold text-slate-400 block mb-1">Título/Descrição:</span>
                <span className="text-xs font-black text-slate-700 block">{selectedItem?.descricao}</span>
                <span className="text-xs font-black text-slate-500 block mb-4 font-mono">
                  Valor: {formatCentavos(selectedItem?.valor_centavos)}
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data de Pagamento *</label>
                <input
                  type="date"
                  required
                  value={baixarData}
                  onChange={(e) => setBaixarData(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Conta Bancária *</label>
                <select
                  required
                  value={baixarConta}
                  onChange={(e) => setBaixarConta(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                >
                  <option value="" disabled>Selecione a conta...</option>
                  {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                <input
                  type="text"
                  value={baixarFormaPagamento}
                  onChange={(e) => setBaixarFormaPagamento(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Pix, Cartão, Dinheiro..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsBaixarModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Confirmar Pagamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ─── MODAL DE TRANSFERÊNCIA ───────────────────────────────────────── */}
      {isTransferModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide font-black">Transferir Saldo entre Contas</h4>
              <button onClick={() => setIsTransferModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleTransferSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Conta Origem (Sai Dinheiro) *</label>
                <select
                  required
                  value={transfOrigem}
                  onChange={(e) => setTransfOrigem(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                >
                  <option value="">Selecione a conta de origem...</option>
                  {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Conta Destino (Entra Dinheiro) *</label>
                <select
                  required
                  value={transfDestino}
                  onChange={(e) => setTransfDestino(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                >
                  <option value="">Selecione a conta de destino...</option>
                  {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Valor (R$) *</label>
                  <input
                    type="text"
                    required
                    value={transfValor}
                    onChange={(e) => setTransfValor(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold font-mono"
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data *</label>
                  <input
                    type="date"
                    required
                    value={transfData}
                    onChange={(e) => setTransfData(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Descrição / Observações</label>
                <input
                  type="text"
                  value={transfDescricao}
                  onChange={(e) => setTransfDescricao(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="EX: Repasse líquido da adquirente"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsTransferModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <ArrowRightLeft size={16} /> Executar Transferência
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
